// ============================================
// VOLATILITY ENGINE — Quantitative Edge Detection
// Implements: Realized Vol, EWMA Forecast, Regime Detection,
//   Mean Reversion Z-Score, Fractional Kelly Sizing
// Inspired by: Sinclair (vol arbitrage), Simons (state detection),
//   Van Tharp (position sizing), RiskMetrics (EWMA)
// ============================================

import { PriceBar, VolatilityMetrics, MarketRegime, RegimeState } from '../types';

// ── Constants ──────────────────────────────────────────
const TRADING_DAYS_PER_YEAR = 252;
const EWMA_LAMBDA = 0.94;         // RiskMetrics standard decay factor
const KELLY_SAFETY = 0.5;         // Half-Kelly for model uncertainty
const KELLY_MAX_FRACTION = 0.15;  // Hard cap: never risk >15% of account
const EDGE_THRESHOLD = 0.03;      // 3% minimum edge to signal
const STRONG_EDGE_THRESHOLD = 0.08; // 8%+ = strong edge

// ── Core Math Utilities ──────────────────────────────────

/** Convert prices to log returns: r(t) = ln(P(t) / P(t-1)) */
function logReturns(closes: number[]): number[] {
  const returns: number[] = [];
  for (let i = 1; i < closes.length; i++) {
    if (closes[i - 1] > 0 && closes[i] > 0) {
      returns.push(Math.log(closes[i] / closes[i - 1]));
    }
  }
  return returns;
}

/** Standard deviation of an array */
function stddev(values: number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / (values.length - 1);
  return Math.sqrt(variance);
}

/** Mean of an array */
function mean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

// ── Realized Volatility Calculations ─────────────────────

/**
 * Close-to-close realized volatility (annualized)
 * RV = stddev(log returns) × √252
 */
function realizedVolCC(closes: number[], window: number): number {
  if (closes.length < window + 1) return 0;
  const recentCloses = closes.slice(-window - 1);
  const returns = logReturns(recentCloses);
  if (returns.length === 0) return 0;
  return stddev(returns) * Math.sqrt(TRADING_DAYS_PER_YEAR) * 100; // as percentage
}

/**
 * Parkinson realized volatility (uses high-low range, more accurate)
 * σ² = (1 / 4n·ln2) × Σ(ln(H/L))²
 * More efficient than close-to-close because it uses intraday range
 */
function realizedVolParkinson(bars: PriceBar[], window: number): number {
  if (bars.length < window) return 0;
  const recentBars = bars.slice(-window);
  const logRanges = recentBars
    .filter(b => b.high > 0 && b.low > 0 && b.high >= b.low)
    .map(b => Math.log(b.high / b.low));
  if (logRanges.length === 0) return 0;

  const sumSquared = logRanges.reduce((sum, lr) => sum + lr * lr, 0);
  const parkVar = sumSquared / (4 * logRanges.length * Math.LN2);
  return Math.sqrt(parkVar * TRADING_DAYS_PER_YEAR) * 100; // as percentage
}

// ── EWMA Volatility Forecast (GARCH Approximation) ──────

/**
 * Exponentially Weighted Moving Average variance forecast
 * σ²(t+1) = λ × σ²(t) + (1-λ) × r²(t)
 * Uses RiskMetrics standard λ = 0.94
 * Returns annualized volatility as percentage
 */
function ewmaForecast(closes: number[]): number {
  const returns = logReturns(closes);
  if (returns.length < 10) return 0;

  // Initialize with sample variance of first 10 returns
  let variance = returns.slice(0, 10).reduce((sum, r) => sum + r * r, 0) / 10;

  // Iterate EWMA through all returns
  for (let i = 10; i < returns.length; i++) {
    variance = EWMA_LAMBDA * variance + (1 - EWMA_LAMBDA) * returns[i] * returns[i];
  }

  // Annualize and convert to percentage
  return Math.sqrt(variance * TRADING_DAYS_PER_YEAR) * 100;
}

// ── Regime Detection (Simplified State Classifier) ──────

/**
 * Classify current market regime using statistical features
 * Replaces HMM with a rules-based classifier that captures the same concept:
 * - Vol ratio (short/long) detects variance expansion/compression
 * - Trend strength detects directional persistence
 * - Combined state routes strategy selection
 */
function detectRegime(bars: PriceBar[], closes: number[]): MarketRegime {
  if (bars.length < 45) {
    return {
      state: 'STABLE',
      confidence: 0.3,
      volRatio: 1.0,
      trendStrength: 0,
      trendDirection: 'NONE',
      description: 'Insufficient data for regime detection',
    };
  }

  // ── Vol ratio: short-term vol / long-term vol ──
  const rv10 = realizedVolCC(closes, 10);
  const rv40 = realizedVolCC(closes, 40);
  const volRatio = rv40 > 0 ? rv10 / rv40 : 1.0;

  // ── Trend strength: consecutive days closing in same direction vs EMA ──
  const ema20 = calculateEMA(closes, 20);
  let trendUp = 0;
  let trendDown = 0;
  for (let i = closes.length - 1; i >= Math.max(0, closes.length - 20); i--) {
    if (ema20[i] !== undefined && closes[i] > ema20[i]) {
      if (trendDown > 0) break;
      trendUp++;
    } else if (ema20[i] !== undefined && closes[i] < ema20[i]) {
      if (trendUp > 0) break;
      trendDown++;
    } else {
      break;
    }
  }
  const trendStrength = Math.max(trendUp, trendDown);
  const trendDirection: 'UP' | 'DOWN' | 'NONE' = trendUp > trendDown ? 'UP' : trendDown > trendUp ? 'DOWN' : 'NONE';

  // ── ADX-like measure: absolute average of recent returns ──
  const recentReturns = logReturns(closes.slice(-20));
  const avgAbsReturn = mean(recentReturns.map(Math.abs));
  const returnBias = mean(recentReturns); // positive = trending up

  // ── Classify regime ──
  let state: RegimeState;
  let confidence: number;
  let description: string;

  if (volRatio > 1.8) {
    state = 'HIGH_VARIANCE';
    confidence = Math.min(0.95, 0.5 + (volRatio - 1.8) * 0.5);
    description = `Volatility expanding rapidly (${volRatio.toFixed(2)}x). Short-term vol >> long-term. Risk-off or event-driven. Sell premium if IV is expensive, or go to cash.`;
  } else if (volRatio > 1.4) {
    state = 'HIGH_VARIANCE';
    confidence = 0.5 + (volRatio - 1.4) * 0.25;
    description = `Elevated volatility (${volRatio.toFixed(2)}x ratio). Market showing stress. Favor credit strategies if edge exists.`;
  } else if (volRatio < 0.6) {
    state = 'COMPRESSING';
    confidence = Math.min(0.9, 0.5 + (0.6 - volRatio));
    description = `Volatility compressing (${volRatio.toFixed(2)}x). Squeeze forming — breakout likely. Consider long gamma positions.`;
  } else if (trendStrength >= 7 && avgAbsReturn > 0.005) {
    state = 'TRENDING';
    confidence = Math.min(0.85, 0.4 + trendStrength * 0.05);
    description = `Strong ${trendDirection === 'UP' ? 'bullish' : 'bearish'} trend (${trendStrength} days). Momentum confirmed. Favor directional debit spreads.`;
  } else if (trendStrength >= 4) {
    state = 'TRENDING';
    confidence = 0.4 + trendStrength * 0.04;
    description = `Moderate ${trendDirection === 'UP' ? 'bullish' : 'bearish'} trend (${trendStrength} days). Directional bias present.`;
  } else if (volRatio >= 0.6 && volRatio <= 1.0 && trendStrength < 3) {
    state = 'MEAN_REVERTING';
    confidence = 0.5;
    description = `Range-bound, low trend strength. Price oscillating around mean. Calendar/diagonal spreads favored.`;
  } else {
    state = 'STABLE';
    confidence = 0.4;
    description = `Normal conditions. Vol ratio ${volRatio.toFixed(2)}x, trend ${trendStrength} days. No strong regime signal.`;
  }

  return { state, confidence, volRatio, trendStrength, trendDirection, description };
}

/** Simple EMA calculation for regime detection */
function calculateEMA(data: number[], period: number): number[] {
  const ema: number[] = [];
  if (data.length === 0) return ema;

  const k = 2 / (period + 1);
  ema[0] = data[0];
  for (let i = 1; i < data.length; i++) {
    ema[i] = data[i] * k + ema[i - 1] * (1 - k);
  }
  return ema;
}

// ── Mean Reversion Z-Score ──────────────────────────────

/**
 * Calculate z-score: how many standard deviations price is from its N-day mean
 * z > 2 = extended/overbought, z < -2 = oversold
 */
function calculateZScore(closes: number[], window: number): { zScore: number; signal: string } {
  if (closes.length < window) {
    return { zScore: 0, signal: 'Insufficient data' };
  }

  const recentCloses = closes.slice(-window);
  const avg = mean(recentCloses);
  const sd = stddev(recentCloses);
  const currentPrice = closes[closes.length - 1];

  if (sd === 0) return { zScore: 0, signal: 'No variance' };

  const zScore = (currentPrice - avg) / sd;

  let signal: string;
  if (zScore > 2.5) {
    signal = `Extremely extended (+${zScore.toFixed(1)}σ) — high mean reversion risk, tighten stops or wait for pullback`;
  } else if (zScore > 2.0) {
    signal = `Extended (+${zScore.toFixed(1)}σ) — approaching overbought, watch for reversal signals`;
  } else if (zScore > 1.0) {
    signal = `Above average (+${zScore.toFixed(1)}σ) — bullish momentum, normal range`;
  } else if (zScore > -1.0) {
    signal = `Near mean (${zScore > 0 ? '+' : ''}${zScore.toFixed(1)}σ) — neutral positioning`;
  } else if (zScore > -2.0) {
    signal = `Below average (${zScore.toFixed(1)}σ) — weak, confirm with momentum before buying`;
  } else if (zScore > -2.5) {
    signal = `Oversold (${zScore.toFixed(1)}σ) — potential bounce, but confirm catalyst before entry`;
  } else {
    signal = `Extremely oversold (${zScore.toFixed(1)}σ) — capitulation territory, high-risk reversal zone`;
  }

  return { zScore, signal };
}

// ── Fractional Kelly Criterion ──────────────────────────

/**
 * Kelly fraction: f = (p × b - q) / b
 * Where: p = win probability, b = win/loss ratio, q = 1-p
 * Applied at half-Kelly for safety, capped at KELLY_MAX_FRACTION
 */
function calculateKelly(
  winProbability: number,
  avgWinAmount: number,
  avgLossAmount: number,
  accountSize: number,
  maxLossPerContract: number
): { kellyFraction: number; halfKelly: number; kellyContracts: number } {
  if (winProbability <= 0 || avgWinAmount <= 0 || avgLossAmount <= 0 || maxLossPerContract <= 0) {
    return { kellyFraction: 0, halfKelly: 0, kellyContracts: 1 };
  }

  const p = Math.min(0.95, Math.max(0.05, winProbability)); // Clamp to 5-95%
  const q = 1 - p;
  const b = avgWinAmount / avgLossAmount; // Win/loss ratio

  // Kelly formula
  let kellyFraction = (p * b - q) / b;

  // If Kelly is negative, the edge doesn't exist — minimum bet
  if (kellyFraction <= 0) {
    return { kellyFraction: 0, halfKelly: 0, kellyContracts: 1 };
  }

  // Apply half-Kelly safety constraint
  const halfKelly = Math.min(kellyFraction * KELLY_SAFETY, KELLY_MAX_FRACTION);

  // Calculate contracts from Kelly-optimal allocation
  const kellyDollars = accountSize * halfKelly;
  const kellyContracts = Math.max(1, Math.floor(kellyDollars / maxLossPerContract));

  return { kellyFraction, halfKelly, kellyContracts };
}

// ── Main Engine: Compute All Volatility Metrics ─────────

/**
 * Core function: takes historical price bars and current IV,
 * returns complete volatility analysis with edge, regime, z-score, and Kelly sizing.
 *
 * This is the "brain" that feeds into the 7-step analysis engine.
 */
export function computeVolatilityMetrics(
  bars: PriceBar[],
  currentIV: number,
  winProbability: number,
  maxProfit: number,
  maxLoss: number,
  accountSize: number
): VolatilityMetrics {
  const closes = bars.map(b => b.close);
  const hasSufficientData = bars.length >= 20;

  // ── Realized Volatility ──
  const rv10d = hasSufficientData ? realizedVolCC(closes, 10) : 0;
  const rv20d = hasSufficientData ? realizedVolCC(closes, 20) : 0;
  const rv40d = bars.length >= 41 ? realizedVolCC(closes, 40) : 0;
  const rv60d = bars.length >= 61 ? realizedVolCC(closes, 60) : 0;
  const parkinsonRV20d = hasSufficientData ? realizedVolParkinson(bars, 20) : 0;

  // ── EWMA Forecast ──
  const forecast = hasSufficientData ? ewmaForecast(closes) : 0;

  // ── Edge Calculation (Sinclair Method) ──
  // Edge = IV - Forecasted RV. Positive means options are overpriced → sell premium
  const bestRV = forecast > 0 ? forecast : (parkinsonRV20d > 0 ? parkinsonRV20d : rv20d);
  const volEdge = currentIV > 0 && bestRV > 0 ? currentIV - bestRV : 0;
  const volEdgePercent = currentIV > 0 ? (volEdge / currentIV) * 100 : 0;

  let edgeSignal: 'SELL_PREMIUM' | 'BUY_PREMIUM' | 'NEUTRAL' = 'NEUTRAL';
  let edgeStrength: 'STRONG' | 'MODERATE' | 'WEAK' | 'NONE' = 'NONE';

  if (volEdge > 0) {
    // IV > RV: options overpriced, sell premium
    edgeSignal = 'SELL_PREMIUM';
    if (volEdgePercent > STRONG_EDGE_THRESHOLD * 100) edgeStrength = 'STRONG';
    else if (volEdgePercent > EDGE_THRESHOLD * 100) edgeStrength = 'MODERATE';
    else edgeStrength = 'WEAK';
  } else if (volEdge < 0) {
    // IV < RV: options underpriced, buy premium
    edgeSignal = 'BUY_PREMIUM';
    if (Math.abs(volEdgePercent) > STRONG_EDGE_THRESHOLD * 100) edgeStrength = 'STRONG';
    else if (Math.abs(volEdgePercent) > EDGE_THRESHOLD * 100) edgeStrength = 'MODERATE';
    else edgeStrength = 'WEAK';
  }

  // ── Regime Detection ──
  const regime = hasSufficientData ? detectRegime(bars, closes) : {
    state: 'STABLE' as RegimeState,
    confidence: 0.3,
    volRatio: 1.0,
    trendStrength: 0,
    trendDirection: 'NONE' as const,
    description: 'Insufficient data for regime classification',
  };

  // ── Z-Score ──
  const { zScore: zScore20d, signal: zScoreSignal } = hasSufficientData
    ? calculateZScore(closes, 20)
    : { zScore: 0, signal: 'Insufficient data' };

  // ── Kelly Criterion ──
  const { kellyFraction, halfKelly, kellyContracts } = calculateKelly(
    winProbability,
    maxProfit,
    maxLoss,
    accountSize,
    maxLoss
  );

  return {
    rv10d: Number(rv10d.toFixed(1)),
    rv20d: Number(rv20d.toFixed(1)),
    rv40d: Number(rv40d.toFixed(1)),
    rv60d: Number(rv60d.toFixed(1)),
    parkinsonRV20d: Number(parkinsonRV20d.toFixed(1)),
    ewmaForecast: Number(forecast.toFixed(1)),
    currentIV,
    volEdge: Number(volEdge.toFixed(1)),
    volEdgePercent: Number(volEdgePercent.toFixed(1)),
    edgeSignal,
    edgeStrength,
    zScore20d: Number(zScore20d.toFixed(2)),
    zScoreSignal,
    regime,
    kellyFraction: Number(kellyFraction.toFixed(4)),
    halfKelly: Number(halfKelly.toFixed(4)),
    kellyContracts,
    dataPoints: bars.length,
    dataSource: bars.length > 0 ? 'alphavantage' : 'synthetic',
  };
}

/**
 * Generate synthetic volatility metrics when no historical data available.
 * Uses IV-based approximations to provide reasonable defaults.
 */
export function syntheticVolMetrics(
  currentIV: number,
  winProbability: number,
  maxProfit: number,
  maxLoss: number,
  accountSize: number
): VolatilityMetrics {
  // Estimate RV from IV with typical variance risk premium (~15% discount)
  const estimatedRV = currentIV * 0.85;

  return {
    rv10d: Number((estimatedRV * 1.1).toFixed(1)),
    rv20d: Number(estimatedRV.toFixed(1)),
    rv40d: Number((estimatedRV * 0.95).toFixed(1)),
    rv60d: Number((estimatedRV * 0.9).toFixed(1)),
    parkinsonRV20d: Number((estimatedRV * 1.05).toFixed(1)),
    ewmaForecast: Number(estimatedRV.toFixed(1)),
    currentIV,
    volEdge: Number((currentIV - estimatedRV).toFixed(1)),
    volEdgePercent: Number((((currentIV - estimatedRV) / Math.max(1, currentIV)) * 100).toFixed(1)),
    edgeSignal: currentIV > estimatedRV * 1.1 ? 'SELL_PREMIUM' : currentIV < estimatedRV * 0.9 ? 'BUY_PREMIUM' : 'NEUTRAL',
    edgeStrength: 'WEAK',
    zScore20d: 0,
    zScoreSignal: 'No historical data — using synthetic estimates',
    regime: {
      state: 'STABLE',
      confidence: 0.2,
      volRatio: 1.0,
      trendStrength: 0,
      trendDirection: 'NONE',
      description: 'Synthetic regime — insufficient historical data for real classification',
    },
    kellyFraction: 0,
    halfKelly: 0,
    kellyContracts: 1,
    dataPoints: 0,
    dataSource: 'synthetic',
  };
}
